import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnChanges, Output, Renderer2 } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import * as _ from 'lodash';
import { Observable } from 'rxjs';

// secondaryButtonIcon should be a material icon name:
// See: https://www.angularjswiki.com/angular/angular-material-icons-list-mat-icon-list/
export interface FormlyDialogData {
  title: string;
  introText?: string;
  saveButtonText?: string;
  cancelButtonText?: string;
  secondaryButtonText?: string;
  secondaryButtonIcon?: string;
  loading$: Observable<boolean>;
  loadingInitData$?: Observable<boolean>;
  errorMessage?: string | undefined;
  disableButton?: boolean;
  ignoreDirty?: boolean;
  formlyConfig: {
    model: any;
    form: UntypedFormGroup;
    fields: FormlyFieldConfig[];
    options: FormlyFormOptions;
  };
}

@Component({
  selector: 'app-form-dialog',
  templateUrl: './formly-dialog.component.html',
  styleUrls: ['./formly-dialog.component.scss']
})
export class FormlyDialogComponent implements OnChanges {

  @Output() saveData = new EventEmitter<FormlyDialogData>();
  @Output() secondaryButtonCalled = new EventEmitter<any>();
  @Output() cancel = new EventEmitter();

  deepCloneData: FormlyDialogData;
  introText = '';
  saveButtonText = 'Save';
  cancelButtonText = 'Cancel';
  secondaryButtonText = '';
  secondaryButtonIcon = 'add';
  errMessageShowing = false;

  constructor(
    public dialogRef: MatDialogRef<FormlyDialogComponent>,
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: FormlyDialogData
  ) {
    this.dialogRef.disableClose = true;
    this.saveButtonText = this.data?.saveButtonText ?? this.saveButtonText;
    this.cancelButtonText = this.data?.cancelButtonText ?? this.cancelButtonText;
    this.secondaryButtonText = this.data?.secondaryButtonText ?? this.secondaryButtonText;
    this.secondaryButtonIcon = this.data?.secondaryButtonIcon ?? this.secondaryButtonIcon;
    this.introText = this.data?.introText ?? this.introText;
    if (this.data) {
      this.deepCloneData = _.cloneDeep(this.data);
      // validation rules may depend on the values of other fields in the model
      // however, formly does not pass the model to validation functions
      // so we must maintain one reference to the model
      // we hopefully understand intuitively that nothing but formly should mutate the model
      // so deep cloning the model is overkill
      this.deepCloneData.formlyConfig.model = this.data.formlyConfig.model;
    }
  }

  ngOnChanges() {
    this.deepCloneData.formlyConfig.fields = this.data.formlyConfig.fields;
    this.cd.markForCheck();

    if (this.data.errorMessage && !this.errMessageShowing) {
      this.renderer.selectRootElement('#topDialog').scrollIntoView({ behavior: 'smooth' });
      this.errMessageShowing = true;
    }
  }

  saveFormData() {
    this.errMessageShowing = false;
    this.saveData.emit(this.deepCloneData);
  }

  cancelFormData() {
    this.cancel.emit();
    this.dialogRef.close();
  }

  secondaryButton() {
    this.secondaryButtonCalled.emit();
  }
}
